import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CA } from 'src/app/shared/entities/ca';
import { SvgIconComponent } from 'src/app/shared/components/svg-icon/svg-icon.component';
import { DatePipe } from '@angular/common';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { MonospacedBlockComponent } from 'src/app/shared/components/monospaced-block/monospaced-block.component';
import { CaRevokeReason } from 'src/app/shared/entities/ca-revoke-reason';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { RevokeIcaDialogComponent } from '../revoke-ica-dialog/revoke-ica-dialog';
import { MatDialog } from '@angular/material/dialog';
import { ShowCrlDialogComponent } from '../show-crl-dialog/show-crl-dialog';
import { map, tap } from 'rxjs';
import { CaType } from 'src/app/shared/entities/ca-type';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-ca-details',
  imports: [
    SvgIconComponent,
    DatePipe,
    MatButtonModule,
    MonospacedBlockComponent,
  ],
  templateUrl: './ca-details.component.html',
  styleUrl: './ca-details.component.scss',
})
export class CaDetailsComponent {
  value = input<CA>();
  crl = input<string>();
  back = output();
  renew = output<CA>();
  revoke = output<{
    ca: CA;
    reason: CaRevokeReason;
    revokeLeafCertificates: boolean;
  }>();
  generateCrl = output<CA>();
  closeCrl = output();

  private showRenewIf = (ca?: CA): boolean => ca?.caType === CaType.SUB; // only ICA
  private showRevokeIf = (ca?: CA): boolean => ca?.caType === CaType.SUB; // only ICA
  private showGenerateCrlIf = (): boolean => true; // any CA

  showRenew = toSignal(toObservable(this.value).pipe(map(this.showRenewIf)));
  showRevoke = toSignal(toObservable(this.value).pipe(map(this.showRevokeIf)));
  showGenerateCrl = toSignal(
    toObservable(this.value).pipe(map(this.showGenerateCrlIf)),
  );

  labels = inject(LangService).lang.features.calist.details;
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);

  title = computed(() => this.labels.title(this.value()?.caName ?? ''));

  onBack() {
    this.back.emit();
  }

  onRenew() {
    const ca = this.value();
    if (ca && ca.caName) {
      this.dialogService.withConfirmation({
        text: this.labels.actions.renew.dialogText(ca.caName),
        title: this.labels.actions.renew.dialogTitle,
      })(() => {
        this.renew.emit(ca);
      });
    }
  }

  onRevoke() {
    const ca = this.value();
    if (ca) {
      this.dialogService.withDialogData<
        { ca: CA },
        { ca: CA; reason: CaRevokeReason; revokeLeafCertificates: boolean }
      >(RevokeIcaDialogComponent, { ca })(data => {
        this.revoke.emit(data);
      });
    }
  }

  onGenerateCrl() {
    const ca = this.value();
    if (ca) {
      this.generateCrl.emit(ca);
    }
  }

  showCrlEffect = () => {
    const crl = this.crl();
    const ca = this.value();
    if (crl) {
      this.dialog
        .open(ShowCrlDialogComponent, { data: { crl, ca } })
        .afterClosed()
        .pipe(tap(() => this.closeCrl.emit()))
        .subscribe();
    }
  };

  constructor() {
    effect(this.showCrlEffect);
  }
}
